/* eslint-disable no-plusplus */
/* eslint-disable jsx-a11y/no-noninteractive-element-to-interactive-role */
import PropTypes from 'prop-types';
import { useRef, useMemo, useEffect } from 'react';
import classNames from 'classnames';

import styles from './CarouselDots.module.scss';

const MAX_DOTS = 7;

const CarouselDots = ({
  nItems,
  onDotClick,
  selectedItem,
  className = '',
  theme = 'light',
  maxDots = MAX_DOTS,
  differentSize = true,
  dotSize = 'small'
}) => {
  const maxItems = Math.min(nItems, maxDots);
  const dotsOffset = Math.floor(maxItems / 2) - 1;

  const containerRef = useRef(null);
  const handleDotClick = index => e => {
    e.preventDefault();
    e.stopPropagation();
    onDotClick(index);
  };

  const handleClick = e => {
    e.preventDefault();
    e.stopPropagation();
  };

  const dots = useMemo(() => {
    const pre = [];
    const show = [];
    const post = [];
    for (let i = 0; i < nItems; i++) {
      if (i < selectedItem - dotsOffset) {
        pre.push(i);
      } else if (i > selectedItem + dotsOffset) {
        post.push(i);
      } else {
        show.push(i);
      }
    }

    return { pre, show, post };
  }, [nItems, selectedItem]);

  useEffect(() => {
    const itemsBeforeMoving = dotsOffset + 1;
    let translateX = 0;

    if (selectedItem <= itemsBeforeMoving) {
      translateX = 0;
    } else if (
      selectedItem > itemsBeforeMoving &&
      selectedItem < nItems - itemsBeforeMoving
    ) {
      // Selected item index minus items to the left or right fixed by the size of each dot
      translateX = (selectedItem - itemsBeforeMoving) * 20;
    } else {
      // Total item spacing minus maxItems spacing
      translateX = nItems * 20 - maxItems * 20;
    }

    containerRef.current.style.transform = `translateX(-${translateX}px)`;
  }, [selectedItem, nItems]);

  return (
    <div
      className={classNames(styles['carousel-dots'], className, {
        [styles[`carousel-dots--size-${dotSize}`]]: dotSize
      })}
      role="button"
      tabIndex={0}
      onClick={handleClick}
    >
      <ul ref={containerRef} className={styles['carousel-dots__wrapper']}>
        {Array.from({ length: nItems }).map((_, index) => (
          <li
            key={index}
            role="button"
            tabIndex={0}
            className={classNames(styles['carousel-dots__dot'], {
              [styles['carousel-dots__dot--selected']]: selectedItem === index,
              [styles['carousel-dots__dot--bigger']]:
                differentSize && dots.show.includes(index),
              [styles[`carousel-dots__dot--${theme}`]]: theme
            })}
            onClick={handleDotClick(index)}
          />
        ))}
      </ul>
    </div>
  );
};

CarouselDots.propTypes = {
  nItems: PropTypes.number.isRequired,
  onDotClick: PropTypes.func.isRequired,
  selectedItem: PropTypes.number.isRequired,
  className: PropTypes.string,
  theme: PropTypes.oneOf(['light', 'dark']),
  dotSize: PropTypes.oneOf(['small', 'medium', 'large']),
  maxDots: PropTypes.number,
  differentSize: PropTypes.bool
};

export default CarouselDots;
